import React, { useState, useMemo } from 'react';
import { hasMinimumRole } from '../../utils/permissions';
import { ClientIdentity } from '../ui';
import './ChatWidget.css';

function ChatDashboard({
  conversations,
  clients,
  currentAdmin,
  onConversationSelect,
  onNewConversation,
  loading,
}) {
  // Determine if current user is trainer or above (manages clients)
  const isAdmin = hasMinimumRole(currentAdmin?.role, 'trainer');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('conversations'); // 'conversations' or 'clients'

  // Filter conversations based on search term. Routes through the
  // same name resolution `getConversationParticipant` uses for
  // display, because `participant.userName` is often stale or
  // literally "undefined undefined" — filtering on the raw field
  // hid conversations whose visible name the trainer was typing.
  // Also matches against the participant's email when available so
  // trainers who remember a client by address can find the thread.
  //
  // Conversations without a `lastMessage` are filtered out
  // regardless of the search term — `handleNewConversation`
  // eagerly creates an empty Conversation row the first time a
  // trainer clicks a client in the All Clients tab, and those
  // empty rows otherwise leak into the Conversations list as
  // "drafted but never sent" threads. Trainers can still resume
  // them by re-clicking the client (handleNewConversation re-
  // selects the existing row).
  const filteredConversations = useMemo(() => {
    const realOnes = (conversations || []).filter((c) => !!c?.lastMessage);
    if (!searchTerm) return realOnes;
    const term = searchTerm.toLowerCase();

    return realOnes.filter((conv) => {
      if (!conv || !conv.participants) return false;
      const participant = getConversationParticipant(conv);
      if (!participant) return false;
      const name = (participant.userName || '').toLowerCase();
      const email = (participant.user?.email || '').toLowerCase();
      return name.includes(term) || email.includes(term);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, searchTerm, currentAdmin.uid, clients]);

  // Filter clients based on search term
  const filteredClients = useMemo(() => {
    if (!searchTerm) return clients;

    return clients.filter((client) => {
      // Add safety checks for client data
      if (!client || !client.user) {
        return false;
      }

      const fullName = `${client.user.name || ''} ${
        client.user.lastName || ''
      }`.toLowerCase();
      const email = client.user.email?.toLowerCase() || '';
      return (
        fullName.includes(searchTerm.toLowerCase()) ||
        email.includes(searchTerm.toLowerCase())
      );
    });
  }, [clients, searchTerm]);

  const formatLastMessage = (conversation) => {
    if (!conversation.lastMessage) return 'No messages yet';

    const message = conversation.lastMessage;
    const isFromAdmin = message.senderId === currentAdmin.uid;
    const prefix = isFromAdmin ? 'You: ' : '';

    return prefix + (message.text || 'Media message');
  };

  const formatLastMessageTime = (conversation) => {
    if (!conversation.lastMessage) return '';

    const date = new Date(conversation.lastMessage.createdAt);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Function declaration (not const arrow) so it's hoisted —
  // filteredConversations useMemo above calls it during the same
  // render and would TDZ-throw if this were `const`.
  function getConversationParticipant(conversation) {
    // Get the user ID from the token (same logic as in FloatingChatWidget)
    const adminUserId = currentAdmin.uid;
    const participant = conversation.participants.find(
      (p) => p.userId !== adminUserId
    );

    if (!participant) return null;

    // Check if userName contains "undefined" or is missing
    const hasUndefinedName =
      !participant.userName || participant.userName.includes('undefined');

    // If participant has nested user object and it's not null, use it to construct the display name
    if (participant.user && typeof participant.user === 'object') {
      const user = participant.user;
      const constructedName =
        `${user.name || ''} ${user.lastName || ''}`.trim() || user.email;

      return {
        ...participant,
        userName: hasUndefinedName ? constructedName : participant.userName,
      };
    }

    // If userName is valid (no "undefined"), use it as-is
    if (!hasUndefinedName) {
      return participant;
    }

    // Last resort: cross-reference with clients to get display name
    const client = clients.find((c) => {
      return c.user?.uid === participant.userId;
    });
    if (client && client.user) {
      const clientName = `${client.user.name || ''} ${
        client.user.lastName || ''
      }`.trim();
      const displayName = clientName || client.user.email;

      if (displayName) {
        return {
          ...participant,
          userName: displayName,
        };
      }
    }

    return participant;
  }

  // Add safety checks for data
  if (!clients || !Array.isArray(clients)) {
    return (
      <div
        style={{
          padding: '20px',
          textAlign: 'center',
          color: 'var(--fg-muted)',
        }}
      >
        Loading clients...
      </div>
    );
  }

  return (
    <div className='chat-dashboard'>
      {/* Search Bar */}
      <div className='dashboard-header'>
        <input
          type='text'
          placeholder='Search conversations or clients...'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className='search-input'
        />
      </div>

      {/* Tabs — type="button" on both so they never get treated as
          form submits in any wrapping form context. */}
      <div className='dashboard-tabs'>
        <button
          type='button'
          onClick={() => setActiveTab('conversations')}
          className={`tab-button ${
            activeTab === 'conversations' ? 'active' : ''
          }`}
        >
          {isAdmin ? 'Conversations' : 'Messages'} (
          {filteredConversations.length})
        </button>
        <button
          type='button'
          onClick={() => setActiveTab('clients')}
          className={`tab-button ${activeTab === 'clients' ? 'active' : ''}`}
        >
          {isAdmin ? 'All Clients' : 'Contact Admin'} ({filteredClients.length})
        </button>
      </div>

      {/* Content — each tab's list wrapper carries a unique `key`
          so React unmounts the inactive branch on switch instead of
          reconciling sibling DOM structurally. The old version had
          two `<div>` wrappers in the same slot with the same shape;
          when their children's keys differed only by item type
          (conversation._id vs client._id) the reconciler could
          retain stale rows across the switch. Explicit keys force
          a clean swap. */}
      <div className='dashboard-content'>
        {loading ? (
          <div
            style={{ padding: '20px', textAlign: 'center', color: 'var(--fg-muted)' }}
          >
            Loading...
          </div>
        ) : activeTab === 'conversations' ? (
          <div key='conversations-list'>
            {renderConversationsList({
              filteredConversations,
              searchTerm,
              getConversationParticipant,
              onConversationSelect,
              formatLastMessage,
              formatLastMessageTime,
            })}
          </div>
        ) : (
          <div key='clients-list'>
            {renderClientsList({
              filteredClients,
              conversations,
              searchTerm,
              onNewConversation,
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Conversations list — renders each conversation as a ClientIdentity
// row (same shape as the All Clients tab) with the last-message
// preview + timestamp + unread badge as trailing chrome. Using the
// shared identity block means the conversations + clients tabs read
// as the same visual family and we don't maintain two parallel
// avatar/name/email layouts.
function renderConversationsList({
  filteredConversations,
  searchTerm,
  getConversationParticipant,
  onConversationSelect,
  formatLastMessage,
  formatLastMessageTime,
}) {
  if (filteredConversations.length === 0) {
    return (
      <div
        style={{
          padding: '20px',
          textAlign: 'center',
          color: 'var(--fg-muted)',
        }}
      >
        {searchTerm ? 'No conversations found' : 'No conversations yet'}
      </div>
    );
  }
  return filteredConversations
    .map((conversation) => {
      const participant = getConversationParticipant(conversation);
      if (!participant) return null;

      const displayName =
        participant.userName ||
        participant.name ||
        participant.user?.email ||
        'Unknown User';

      return (
        <div
          key={conversation._id}
          onClick={() => onConversationSelect(conversation)}
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid var(--border-soft)',
            cursor: 'pointer',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <ClientIdentity
              className='ci-grow'
              name={displayName}
              email={participant.user?.email}
              avatar={participant.user?.avatar}
              size={40}
            />
            <div
              style={{
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: 4,
              }}
            >
              <div style={{ fontSize: '12px', color: 'var(--fg-muted)' }}>
                {formatLastMessageTime(conversation)}
              </div>
              {conversation.unreadCount > 0 ? (
                <div
                  style={{
                    backgroundColor: '#ef4444',
                    color: 'white',
                    borderRadius: '50%',
                    width: '18px',
                    height: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    fontWeight: 'bold',
                  }}
                >
                  {conversation.unreadCount > 99
                    ? '99+'
                    : conversation.unreadCount}
                </div>
              ) : null}
            </div>
          </div>
          <div
            style={{
              fontSize: '12px',
              color: 'var(--fg-muted)',
              marginTop: 4,
              marginLeft: 52,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {formatLastMessage(conversation)}
          </div>
        </div>
      );
    })
    .filter(Boolean);
}

// All Clients list — every client in the trainer's roster with a
// "Has chat" / "New chat" indicator. Gated on lastMessage so the
// indicator reflects "we actually exchanged messages", not just
// "an empty Conversation row was created on click".
function renderClientsList({
  filteredClients,
  conversations,
  searchTerm,
  onNewConversation,
}) {
  if (filteredClients.length === 0) {
    return (
      <div
        style={{
          padding: '20px',
          textAlign: 'center',
          color: 'var(--fg-muted)',
        }}
      >
        {searchTerm ? 'No clients found' : 'No clients available'}
      </div>
    );
  }
  return filteredClients
    .filter((client) => client && client.user)
    .map((client) => {
      const fullName = `${client.user.name || ''} ${client.user.lastName || ''}`.trim();
      const clientUserId = client.user.uid;
      const hasConversation = conversations.some(
        (conv) =>
          !!conv.lastMessage &&
          conv.participants.some((p) => p.userId === clientUserId),
      );
      return (
        <div
          key={client._id}
          onClick={() => onNewConversation(client)}
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid var(--border-soft)',
            cursor: 'pointer',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <ClientIdentity
              className='ci-grow'
              name={fullName || client.user.email || 'Unknown User'}
              email={client.user.email}
              avatar={client.user.avatar}
              gradient={hasConversation ? '#10b981' : 'var(--primary, #8b5cf6)'}
              size={40}
            />
            <div
              style={{
                flexShrink: 0,
                fontSize: '12px',
                color: hasConversation ? '#10b981' : 'var(--fg-muted)',
              }}
            >
              {hasConversation ? 'Has chat' : 'New chat'}
            </div>
          </div>
        </div>
      );
    });
}

export default ChatDashboard;
